'use client';

import type { SpecialProps } from './types';
import { QuestionName, stripQuestionHtml } from '@/components/registration/question-name';
import { formatPrice } from '@/utils/locale';

const LABEL_CLS = 'text-sm font-bold text-foreground';

/**
 * Fallback genérico para preguntas con specialQuestion=true cuyo internalAlias no
 * está registrado en specialRegistry. Soporta los tipos básicos: texto (1) y
 * opciones (radio 8 / dropdown 7) — el contenido visual es deliberadamente neutro.
 */
export default function GenericSpecial({ question, value, onChange, priceFor, eventCurrency }: SpecialProps) {
  const isOptionType = question.questionTypeId === 8 || question.questionTypeId === 7;
  const isTextType = question.questionTypeId === 1;
  const options = (question.questionOptionList ?? []).filter(o => o.enabled !== false);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-black text-foreground">
          <QuestionName name={question.name} />
          {question.mandatory && <span className="text-destructive ml-0.5">*</span>}
        </h2>
        {question.helpText && (
          <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: question.helpText }} />
        )}
      </div>

      {isOptionType && (
        <div className="flex flex-col gap-3">
          {options.map(opt => {
            const isSelected = value != null && String(value) === String(opt.questionOptionId);
            return (
              <label
                key={opt.questionOptionId}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/40'
                }`}
              >
                <input
                  type="radio"
                  className="h-4 w-4 accent-primary shrink-0"
                  checked={isSelected}
                  onChange={() => onChange(String(opt.questionOptionId))}
                />
                <span className={`flex-1 text-sm text-foreground ${isSelected ? 'font-semibold' : ''}`}>
                  {opt.name}
                </span>
                {opt.price ? (
                  <span className="text-sm font-semibold text-primary shrink-0">{formatPrice(priceFor(opt), eventCurrency)}</span>
                ) : null}
              </label>
            );
          })}
        </div>
      )}

      {isTextType && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`special_${question.questionId}`} className={LABEL_CLS}><QuestionName name={question.name} /></label>
          <input
            id={`special_${question.questionId}`}
            type="text"
            aria-label={stripQuestionHtml(question.name)}
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            maxLength={question.maxTextLength && question.maxTextLength > 0 ? question.maxTextLength : undefined}
            className="w-full rounded-xl border border-input focus:border-primary focus:ring-primary/10 bg-background py-3 px-4 text-sm text-foreground focus:outline-none focus:ring-4 transition-all"
          />
        </div>
      )}
    </div>
  );
}
